import AttachmentIcon from "@mui/icons-material/Attachment";
import { Box, Stack, SxProps, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

import { DropzoneErrorDisplay } from "./DropzoneErrorDisplay";

export type DropzoneProps = {
  label: string;
  dragActiveText: string;
  onDrop: (acceptedFiles: File[]) => void;
  /**
   * file types including the period
   * e.g. [".csv", ".xlsx"]
   */
  accept?: string | string[];
  sx?: SxProps;
  disabled?: boolean;
  multiple?: boolean;
  displayUploadErrors?: boolean;
  onDropRejected?: (rejections: FileRejection[]) => void;
};

export function Dropzone(props: DropzoneProps) {
  const [showDropRejected, setShowDropRejected] = useState(false);
  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);
  const { multiple = true, displayUploadErrors = true } = props;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    useFsAccessApi: false,
    onDrop: props.onDrop,
    accept: props.accept,
    onDropRejected: (rejections: FileRejection[]) => {
      setDropRejectedFiles(rejections);
      props.onDropRejected?.(rejections);
    },
    onDropAccepted: () => setShowDropRejected(false),
    disabled: props.disabled,
    multiple,
    maxFiles: multiple ? undefined : 1,
  });
  const theme = useTheme();

  return (
    <>
      {displayUploadErrors &&
        dropRejectedFiles.map(({ file, errors }: FileRejection, index) => (
          <DropzoneErrorDisplay
            key={index}
            file={file}
            index={index}
            errors={errors}
            setDropRejectedFiles={setDropRejectedFiles}
            dropRejectedFiles={dropRejectedFiles}
          />
        ))}

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: theme.palette.divider,
          padding: 2,
          borderRadius: 1,
          bgcolor: theme.palette.surfaceSubtle,
          color: theme.palette.text.secondary,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": {
            bgcolor: "background.default",
          },
          height: theme.spacing(12),
          ...props.sx,
        }}
      >
        <input {...getInputProps()} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", height: "100%" }}
        >
          <AttachmentIcon sx={{ transform: "rotate(90deg)" }} />
          <Typography variant="body2">
            {isDragActive ? props.dragActiveText : props.label}
          </Typography>
        </Stack>
      </Box>
    </>
  );
}
